import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useStorage } from '@vueuse/core'
import { useAuthStore } from '@/stores/auth'
import {
  getNotificationSettings,
  updateNotificationSettings,
  getNotifications,
  markNotificationAsRead,
  markNotificationAsUnread,
  markAllNotificationsAsRead,
  deleteNotification,
  deleteAllNotifications
} from '@/api/notifications'
import { showInfo } from '@/services/notification'

export const useNotificationStore = defineStore('notifications', () => {
  const authStore = useAuthStore()
  
  const notifications = ref([])
  const settings = ref(null)
  const loading = ref(false)
  const unreadCount = ref(0)
  const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  
  // Globales Polling
  const pollingInterval = ref(null)
  const isPolling = ref(false)
  const shownNotificationIds = useStorage('shown_notification_ids', new Set())

  
  const hasUnreadNotifications = computed(() => unreadCount.value > 0)
  const unreadNotifications = computed(() => 
    notifications.value.filter(notification => !notification.readAt)
  )

  
  const fetchNotifications = async (params = {}) => {
    try {
      loading.value = true
      const response = await getNotifications({
        page: pagination.value.page,
        limit: pagination.value.limit,
        ...params
      })
      
      if (response.data.success) {
        const newNotifications = response.data.data.notifications || []
        
        const shouldShowToast = params.showToast && !authStore.isImpersonated()

        // Prüfe auf neue Benachrichtigungen für showInfo (nur bei Polling)
        if (
          shouldShowToast &&
          notifications.value.length > 0 &&
          newNotifications.length > 0 &&
          !params.isInitialLoad
        ) {
          const existingIds = new Set(notifications.value.map((notification) => notification.id))
          const newNotification = newNotifications.find(
            (notification) =>
              !existingIds.has(notification.id) &&
              !notification.readAt &&
              !shownNotificationIds.value.has(notification.id),
          )

          if (newNotification) {
            showInfo(newNotification.message, `${newNotification.title}`, 2500)
            shownNotificationIds.value.add(newNotification.id)
          }
        }
        
        // Bei initialem Laden alle ungelesenen Benachrichtigungen als gezeigt markieren
        if (params.isInitialLoad) {
          newNotifications.forEach((n) => {
            if (!n.readAt) {
              shownNotificationIds.value.add(n.id)
            }
          })
        }
        
        notifications.value = newNotifications
        unreadCount.value = response.data.unreadCount || 0
        if (response.data.data.pagination) {
          pagination.value = {
            ...pagination.value,
            ...response.data.data.pagination
          }
        }
        
        // Bereinige gezeigte IDs - entferne IDs die nicht mehr existieren
        const currentNotificationIds = new Set(newNotifications.map((n) => n.id))
        const filteredIds = Array.from(shownNotificationIds.value).filter((id) =>
          currentNotificationIds.has(id),
        )
        shownNotificationIds.value = new Set(filteredIds)
      }
      
      return response.data
    } catch (error) {
      console.error('Error fetching notifications:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const fetchNotificationSettings = async () => {
    try {
      const response = await getNotificationSettings()
      if (response.data.success) {
        settings.value = response.data.data
      }
      return response.data
    } catch (error) {
      console.error('Error fetching notification settings:', error)
      throw error
    }
  }

  const updateSettings = async (newSettings) => {
    try {
      const response = await updateNotificationSettings(newSettings)
      if (response.data.success) {
        settings.value = { ...settings.value, ...newSettings }
      }
      return response.data
    } catch (error) {
      console.error('Error updating notification settings:', error)
      throw error
    }
  }

  const markAsRead = async (notificationId) => {
    try {
      const response = await markNotificationAsRead(notificationId)
      if (response.data.success) {
        
        const notification = notifications.value.find(n => n.id === notificationId)
        if (notification) {
          notification.readAt = new Date().toISOString()
        }
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
      return response.data
    } catch (error) {
      console.error('Error marking notification as read:', error)
      throw error
    }
  }

  const markAsUnread = async (notificationId) => {
    try {
      const response = await markNotificationAsUnread(notificationId)
      if (response.data.success) {
        
        const notification = notifications.value.find(n => n.id === notificationId)
        if (notification) {
          notification.readAt = null
        }
        unreadCount.value += 1
      }
      return response.data
    } catch (error) {
      console.error('Error marking notification as unread:', error)
      throw error
    }
  }

  const markAllAsRead = async () => {
    try {
      const response = await markAllNotificationsAsRead()
      if (response.data.success) {
        
        notifications.value.forEach(notification => {
          notification.readAt = new Date().toISOString()
        })
        unreadCount.value = 0
      }
      return response.data
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      throw error
    }
  }

  const removeNotification = async (notificationId) => {
    try {
      const response = await deleteNotification(notificationId)
      if (response.data.success) {
        
        const index = notifications.value.findIndex(n => n.id === notificationId)
        if (index !== -1) {
          const notification = notifications.value[index]
          notifications.value.splice(index, 1)
          
          
          if (!notification.readAt) {
            unreadCount.value = Math.max(0, unreadCount.value - 1)
          }
        }
      }
      return response.data
    } catch (error) {
      console.error('Error deleting notification:', error)
      throw error
    }
  }

  const removeAllNotifications = async () => {
    try {
      const response = await deleteAllNotifications()
      if (response.data.success) {
        
        notifications.value = []
        unreadCount.value = 0
      }
      return response.data
    } catch (error) {
      console.error('Error deleting all notifications:', error)
      throw error
    }
  }



  const addNotification = (notification) => {
    
    notifications.value.unshift(notification)
    
    
    if (!notification.readAt) {
      unreadCount.value += 1
    }
  }

  const updateNotification = (notificationId, updates) => {
    const notification = notifications.value.find(n => n.id === notificationId)
    if (notification) {
      const wasUnread = !notification.readAt
      Object.assign(notification, updates)
      
      
      if (wasUnread && notification.readAt) {
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      } else if (!wasUnread && !notification.readAt) {
        unreadCount.value += 1
      }
    }
  }

  const setPage = (page) => {
    pagination.value.page = page
  }

  const setLimit = (limit) => {
    pagination.value.limit = limit
  }

  // Globale Polling-Funktionen
  const startPolling = () => {
    if (isPolling.value || authStore.isImpersonated()) return
    
    isPolling.value = true
    pollingInterval.value = setInterval(async () => {
      try {
        if (authStore.isImpersonated()) {
          stopPolling()
          return
        }
        await fetchNotifications({ limit: 10, unreadOnly: false, showToast: true })
      } catch (error) {
        console.error('Error during polling:', error)
      }
    }, 30000) // Alle 30 Sekunden
  }

  const stopPolling = () => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value)
      pollingInterval.value = null
    }
    isPolling.value = false
  }

  const initializeNotifications = async () => {
    try {
      await fetchNotifications({ limit: 10, unreadOnly: false, isInitialLoad: true })
      startPolling()
    } catch (error) {
      console.error('Error initializing notifications:', error)
    }
  }

  const reset = () => {
    notifications.value = []
    settings.value = null
    loading.value = false
    unreadCount.value = 0
    pagination.value = {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    }
    stopPolling()
  }

  return {
    
    notifications,
    settings,
    loading,
    unreadCount,
    pagination,
    isPolling,
    
    
    hasUnreadNotifications,
    unreadNotifications,
    
    
    fetchNotifications,
    fetchNotificationSettings,
    updateSettings,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    removeNotification,
    removeAllNotifications,
    addNotification,
    updateNotification,
    setPage,
    setLimit,
    startPolling,
    stopPolling,
    initializeNotifications,
    reset
  }
})
